import React from 'react';

/**
 * Shown when /rubrics POST returns 409.
 *
 * Two modes:
 *   - Confirm second (canCreateSecond): one matching rubric already exists.
 *     Teacher can cancel, open the existing one, or confirm they want two
 *     different rubrics for this single class on the same day.
 *   - Cap reached: two matching rubrics already exist. Cancel or open
 *     an existing one — a third is not allowed.
 */
export default function DuplicateRubricDialog({
  open,
  message,
  canCreateSecond = false,
  creatingSecond = false,
  onCancel,
  onViewExisting,
  onCreateSecond
}) {
  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(2px)'
    }}>
      <div style={{
        background: 'white',
        padding: 30,
        borderRadius: 12,
        maxWidth: 560,
        width: '92%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        border: `3px solid ${canCreateSecond ? '#1565c0' : '#e65100'}`,
        animation: 'dupRubricFadeIn 0.2s ease-out'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <div style={{
            width: 50, height: 50, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 'bold',
            background: canCreateSecond ? '#E3F2FD' : '#FFF3E0',
            color: canCreateSecond ? '#1565c0' : '#e65100'
          }}>{canCreateSecond ? '?' : '!'}</div>
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 'bold',
            color: canCreateSecond ? '#1565c0' : '#e65100'
          }}>
            {canCreateSecond ? 'Create a second rubric?' : 'Two rubrics already exist'}
          </h3>
        </div>

        <p style={{
          margin: '0 0 14px 0',
          fontSize: 15,
          lineHeight: 1.5,
          color: '#333',
          whiteSpace: 'pre-wrap'
        }}>
          {message || (canCreateSecond
            ? 'A rubric with the same class, subject, and date already exists. Do you want to create 2 different rubrics for this single class?'
            : 'Two rubrics with the same class, subject, and date already exist.')}
        </p>

        {canCreateSecond ? (
          <p style={{ margin: '0 0 22px 0', fontSize: 14, color: '#555' }}>
            Confirming keeps the existing rubric and saves this one as a
            second, different rubric for the same class on the same day.
            You can have at most two.
          </p>
        ) : (
          <p style={{ margin: '0 0 22px 0', fontSize: 14, color: '#555' }}>
            A third rubric for this class on this day is not allowed. Click{' '}
            <b>Open existing rubric</b> to copy your new content to the
            clipboard and open Saved Rubrics in a new tab — paste it in
            and update there.
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={onCancel}
            disabled={creatingSecond}
            style={{ ...btnSecondary, opacity: creatingSecond ? 0.6 : 1 }}
            onMouseOver={(e) => { if (!creatingSecond) e.target.style.background = '#f5f5f5'; }}
            onMouseOut={(e) => { e.target.style.background = 'white'; }}
          >
            Cancel
          </button>
          <button
            onClick={onViewExisting}
            disabled={creatingSecond}
            style={{ ...btnPrimary, opacity: creatingSecond ? 0.6 : 1 }}
            onMouseOver={(e) => { if (!creatingSecond) e.target.style.opacity = '0.9'; }}
            onMouseOut={(e) => { e.target.style.opacity = creatingSecond ? 0.6 : '1'; }}
          >
            Open existing rubric
          </button>
          {canCreateSecond && (
            <button
              onClick={onCreateSecond}
              disabled={creatingSecond}
              style={{
                ...btnConfirm,
                width: '100%',
                flex: '1 0 100%',
                opacity: creatingSecond ? 0.7 : 1,
                cursor: creatingSecond ? 'wait' : 'pointer'
              }}
              onMouseOver={(e) => { if (!creatingSecond) e.target.style.opacity = '0.9'; }}
              onMouseOut={(e) => { e.target.style.opacity = creatingSecond ? 0.7 : '1'; }}
            >
              {creatingSecond ? 'Saving…' : 'Yes, create second rubric'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes dupRubricFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

const btnBase = {
  flex: 1,
  minWidth: 140,
  padding: '12px 18px',
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s'
};
const btnSecondary = {
  ...btnBase,
  background: 'white',
  color: '#555',
  border: '2px solid #bbb'
};
const btnPrimary = {
  ...btnBase,
  background: '#2196F3',
  color: 'white',
  border: 'none'
};
const btnConfirm = {
  ...btnBase,
  background: '#2e7d32',
  color: 'white',
  border: 'none'
};
